import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { CallService } from 'src/app/core/services/call.service';
import { UserService } from 'src/app/core/services/user.service';
import {
  USER_TYPES,
  STATUSES,
  RECORDING_STATUSES,
} from 'src/app/shared/constants/common.constants';
import { Call, CallDetailsResponse, CallRecordingResponse } from 'src/app/shared/model/call.model';
import {
  CallDispute,
  CallDisputeData,
  CallDisputeDetailsResponse,
  DisputeStatusResponse,
  DisputeUpdateResponse,
} from 'src/app/shared/model/dispute.model';
import { UserDetailsResponse } from 'src/app/shared/model/user.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableModule } from 'primeng/table';
import { domainUrl } from 'src/environments/environment';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { DisputeService } from 'src/app/core/services/dispute.service';
import { ButtonModule } from 'primeng/button';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
  selector: 'app-call-details',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    HttpClientModule,
    ToastModule,
    DropdownModule,
    TableModule,
    ButtonModule,
  ],
  providers: [TranslateService],
  templateUrl: './call-details.component.html',
  styleUrls: ['../../shared/styles/dispute-details.scss', './call-details.component.scss'],
})
export class CallDetailsComponent implements OnInit {
  statuses = STATUSES;

  userTypeConstants = USER_TYPES;

  userType: string | null = null;

  callData: CallDetailsResponse | null = null;

  baseApiUrl = domainUrl.url;

  callerAvatar: string | null = null;

  talkingBuddyAvatar: string | null = null;

  callId: string | null = null;

  disputeId: string | null = null;

  callDisputeResponse: CallDisputeDetailsResponse['data'] | null = null;

  disputeData: CallDispute[] = [];

  callDisputeCounts: CallDisputeData['statusCounts'] | null = null;

  currentIndex = 0;

  disputeStatus: string | null = null;

  disputeUpdateStatus: DisputeUpdateResponse['data'] | null = null;

  recordingStatuesList = RECORDING_STATUSES;

  recordingStatus = RECORDING_STATUSES.NOT_INITIATED;

  recordingData: CallRecordingResponse['data'] | null = null;

  recordingURL: string | null = null;

  disputeStatuses: DisputeStatusResponse['data'] | [] = [];

  disputeComment = '';

  constructor(
    private callService: CallService,
    private userService: UserService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private toastService: ToasterService,
    private disputeService: DisputeService,
  ) {}

  ngOnInit(): void {
    this.callId = this.route.snapshot.paramMap.get('callId');
    this.disputeId = this.route.snapshot.paramMap.get('disputeId');

    if (this.callId) {
      this.fetchCallData(this.callId);
      this.fetchDisputeData(this.callId);
      this.getDisputeStatuses();
    }
  }

  getDisputeStatuses() {
    this.disputeService.getDisputeStatus().subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.disputeStatuses = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'DISPUTE_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_STATUS',
            ),
          );
        }
      },
    });
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  getCallRecordingForDownloadMP4(callId: string) {
    if (this.recordingStatus !== this.recordingStatuesList.COMPLETED) {
      this.recordingStatus = this.recordingStatuesList.IN_PROGRESS;

      const interval = setInterval(() => {
        this.callService.getCallRecordingForDownloadMP4(callId).subscribe({
          next: (response) => {
            if (response.status === STATUSES.SUCCESS) {
              this.recordingData = response.data;
              this.recordingStatus = this.recordingData.recordingConversionStatus;

              if (this.recordingStatus === this.recordingStatuesList.COMPLETED) {
                clearInterval(interval);
                this.recordingURL = this.recordingData.recordingFileUrl;
                this.downloadRecording(
                  this.recordingURL || '',
                  this.translateService.instant('CALL_DETAILS_PAGE.RECORDING_CALL_FILENAME'),
                );
                this.toastService.showSuccessToast(this.translateService.instant(response.message));

                // Reset after completion
                setTimeout(() => {
                  this.recordingStatus = this.recordingStatuesList.NOT_INITIATED;
                }, 10000);
              } else if (this.recordingStatus === this.recordingStatuesList.FAILED) {
                clearInterval(interval);
                this.recordingURL = null;
                this.toastService.showErrorToast(this.translateService.instant(response.message));
              } else if (this.recordingStatus === this.recordingStatuesList.IN_PROGRESS) {
                this.recordingURL = null;
                this.toastService.showWarningToast(this.translateService.instant(response.message));
              }
            }
          },
          error: (err) => {
            clearInterval(interval);
            this.recordingStatus = this.recordingStatuesList.FAILED;

            if (err.status === 404) {
              this.toastService.showInfoToast(
                this.translateService.instant('CALL_DETAILS_PAGE.TOAST_MESSAGE.NO_RECORDING_INFO'),
              );
            } else {
              this.toastService.showErrorToast(this.translateService.instant(err.error.Message));
            }

            setTimeout(() => {
              this.recordingStatus = this.recordingStatuesList.NOT_INITIATED;
            }, 10000);
          },
        });
      }, 3000); // Poll every 3 seconds
    }
  }

  // Need to do some improvements later when fix from backend (video should download directly instead of open in new tab)
  downloadRecording(url: string, fileName: string) {
    CommonService.downloadFile(url, fileName);
  }

  fetchCallData(callId: string) {
    this.callService.getCallById(callId).subscribe({
      next: (response) => {
        this.callData = response;
        this.fetchCallerAvatar(this.callData.data);
        this.fetchTalkingBuddyAvatar(this.callData.data);
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_CALL_DATA',
            ),
          );
        }
      },
    });
  }

  fetchDisputeData(callId: string) {
    this.callService.getDisputesByCallId(callId).subscribe({
      next: (response) => {
        this.callDisputeResponse = response.data;
        this.disputeData = this.callDisputeResponse.disputes;
        // Find the dispute's index using the disputeId
        const index = this.disputeData.findIndex(
          (dispute) => dispute.id === (this.disputeId && parseInt(this.disputeId, 10)),
        );
        this.currentIndex = index >= 0 ? index : 0;
        this.callDisputeCounts = this.callDisputeResponse.statusCounts;
        this.disputeData.forEach((dispute) => {
          this.fetchUserDetails(dispute);
        });
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_DATA',
            ),
          );
        }
      },
    });
  }

  updateDisputeStatus(dispute: CallDispute, disputeComment: string) {
    const disputeData = dispute;
    const payload = {
      disputeStatus:
        dispute.disputeStatus === this.statuses.UNDER_REVIEW
          ? this.statuses.UNDER_REVIEW_VALUE
          : dispute.disputeStatus,
      reviewerComment: disputeComment || '',
    };
    this.disputeService.updateDisputeType(dispute.id, payload).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.disputeUpdateStatus = response.data;
          disputeData.disputeStatus = this.disputeUpdateStatus.disputeStatus;
          this.fetchDisputeData(this.callId || '');
          this.toastService.showSuccessToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.UPDATE_DISPUTE_STATUS_SUCCESSFULLY',
            ),
          );
          this.disputeComment = '';
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_UPDATE_DISPUTE_STATUS',
            ),
          );
        }
      },
    });
  }

  fetchUserDetails(dispute: CallDispute) {
    const disputeData = dispute;
    this.userService.getUserDetailsById(dispute.raisedById.toString()).subscribe({
      next: (userResponse: UserDetailsResponse) => {
        const user = userResponse.data;
        this.userType = user.userType;
        disputeData.userType = this.userType;
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_DATA',
            ),
          );
        }
      },
    });
  }

  fetchCallerAvatar(call: Call) {
    this.userService.getUserDetailsById(call.callerId.toString()).subscribe({
      next: (userResponse: UserDetailsResponse) => {
        const user = userResponse.data;
        // eslint-disable-next-line no-param-reassign
        this.callerAvatar = user.avatar;
      },
    });
  }

  fetchTalkingBuddyAvatar(call: Call) {
    this.userService.getUserDetailsById(call.talkingBuddyId.toString()).subscribe({
      next: (userResponse: UserDetailsResponse) => {
        const user = userResponse.data;
        // eslint-disable-next-line no-param-reassign
        this.talkingBuddyAvatar = user.avatar;
      },
    });
  }

  formatDuration(duration: string): string {
    if (!duration) {
      return duration;
    }

    const [hours, minutes, seconds] = duration.split(':');
    return parseInt(hours, 10) === 0 ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
  }

  previous() {
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
  }

  next() {
    if (this.currentIndex < this.disputeData.length - 1) {
      this.currentIndex += 1;
    }
  }
}
